import errorTracker from '@shared/errorTracking/errorTracker';
import { type ComponentType, lazy } from 'react';

const RELOAD_AT_KEY = 'lazy-chunk-reload-at';
// A failure this soon after our own reload means the chunk is genuinely broken.
const RELOAD_GUARD_MS = 10_000;

const getLastReloadAt = (): number => {
  try {
    return Number(sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
  } catch {
    return 0;
  }
};

/** Returns false when the marker could not be persisted (storage blocked or full). */
const setLastReloadAt = (value: number): boolean => {
  try {
    sessionStorage.setItem(RELOAD_AT_KEY, String(value));

    return sessionStorage.getItem(RELOAD_AT_KEY) === String(value);
  } catch {
    return false;
  }
};

/**
 * `React.lazy` that recovers from stale chunks after a deploy.
 *
 * Every deploy removes the previous build's hashed chunks from S3, and CloudFront
 * answers the missing file with index.html (200 text/html). A page opened before
 * the deploy then fails its next lazy import (e.g. navigating to the payment
 * success page) and lands on the global error boundary. On such a failure we do
 * a single full reload of the current URL so the fresh index.html and chunks are
 * fetched; history state (e.g. paymentBreakdown) survives the reload. The
 * timestamp guard prevents a reload loop if the chunk is genuinely broken.
 */
export const lazyWithReload = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) =>
  lazy(async () => {
    try {
      return await factory();
    } catch (error) {
      const now = Date.now();

      // Without a persisted marker the next load could not detect a loop, so only
      // reload once the marker is stored; otherwise fall through to the error boundary.
      if (now - getLastReloadAt() < RELOAD_GUARD_MS || !setLastReloadAt(now)) {
        throw error;
      }

      // Recovered by the reload below, so report it here to keep it visible in Sentry.
      errorTracker.captureException(error instanceof Error ? error : new Error(String(error)));
      window.location.reload();

      // Keep Suspense pending until the reload takes over.
      return new Promise<{ default: T }>(() => {});
    }
  });
